import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, ArrowLeft, Plus, Minus, ShoppingBag, Copy, Check, Trash2, X, ArrowRight } from "lucide-react";
import { MENU, BRAND } from "../../data/content";
import { useCart, slugify } from "../../context/CartContext";
import { useAvailability } from "../../hooks/useAvailability";
import Footer from "./Footer";
import RatingPopup from "./RatingPopup";

const cartId = (name, label) => slugify(`${name} ${label}`);
const fmt = (n) => `$${n.toFixed(2)}`;

const Row = ({ item, n, items, soldOut, onAdd }) => (
  <div className="flex items-start gap-4 border-b border-white/8 py-4" data-testid="order-menu-item">
    <span className="mt-0.5 w-7 shrink-0 font-display text-sm font-semibold text-gold/50">
      {String(n).padStart(2, "0")}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-display text-lg font-semibold text-cream">{item.name}</h4>
        {item.badge && (
          <span className="rounded-full border border-ember/40 bg-ember/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ember">
            {item.badge}
          </span>
        )}
      </div>
      <p className="mt-1 max-w-xl text-sm leading-relaxed text-cream/55">{item.desc}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {item.prices.map(([label, val], i) => {
          const id = cartId(item.name, label);
          const qty = items[id]?.qty || 0;
          if (soldOut.has(id)) {
            return (
              <span key={i} data-testid={`soldout-${id}`}
                className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1.5 text-sm font-semibold text-red-300/80">
                <span className="font-medium">{label}</span> Sold out
              </span>
            );
          }
          return (
            <button
              key={i}
              onClick={() => onAdd(item, label, val)}
              data-testid={`order-add-${id}`}
              className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${
                qty > 0
                  ? "border-gold bg-gold text-ink"
                  : "border-white/12 bg-white/[0.03] text-cream hover:border-gold/40 hover:-translate-y-0.5"
              }`}
            >
              <span className={qty > 0 ? "text-ink/70 font-medium" : "text-cream/40 font-medium"}>{label}</span>
              ${val}
              {qty > 0 ? (
                <span className="ml-1 grid h-5 min-w-5 place-items-center rounded-full bg-ink/20 px-1 text-xs">{qty}</span>
              ) : (
                <Plus size={14} className="opacity-60 group-hover:opacity-100" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

export const OrderPage = () => {
  const { items, lines, count, subtotal, add, setQty, clear, openCheckout } = useCart();
  const soldOut = useAvailability();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const ratingScheduled = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Order for Pickup · Smokey & Creamy";
  }, []);

  // 50s after the FIRST item is added, show the Google rating prompt for 15s (once).
  const scheduleRating = () => {
    if (ratingScheduled.current) return;
    ratingScheduled.current = true;
    setTimeout(() => {
      setShowRating(true);
      setTimeout(() => setShowRating(false), 15000);
    }, 50000);
  };

  const handleAdd = (item, label, val) => {
    scheduleRating();
    add(item.name, label, val);
  };

  const goCheckout = () => { setOpen(false); openCheckout(); };

  const copySummary = async () => {
    const text = [
      "Smokey & Creamy — My Pickup Order",
      ...lines.map((v) => `${v.qty}x ${v.name}${v.label && v.label !== "Reg" && v.label !== "Each" ? ` (${v.label})` : ""} — ${fmt(v.qty * v.price)}`),
      `Total: ${fmt(subtotal)}`,
      `Order online or call: ${BRAND.phoneDisplay}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="relative min-h-screen bg-ink text-cream">
      {/* Top bar */}
      <header className="sticky top-0 z-[100] glass border-b border-gold/15">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 sm:px-8 h-[72px]">
          <Link to="/" data-testid="order-back-home" className="flex items-center gap-2 text-sm font-medium text-cream/75 hover:text-gold transition-colors">
            <ArrowLeft size={18} /> <span className="hidden sm:inline">Back to site</span>
          </Link>
          <Link to="/" className="flex items-center gap-2.5">
            <img src={BRAND.logo} alt="Smokey & Creamy" className="h-11 w-11 rounded-full object-cover ring-2 ring-gold/50" />
            <span className="hidden sm:block text-gold-gloss font-display text-lg font-semibold">Smokey &amp; Creamy</span>
          </Link>
          <a href={BRAND.phoneHref} data-testid="order-topbar-call" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-cream/80 transition-colors hover:border-gold/40 hover:text-cream">
            <Phone size={16} /> <span className="hidden sm:inline">{BRAND.phoneDisplay}</span><span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>

      {/* Intro */}
      <section className="mx-auto max-w-5xl px-5 sm:px-8 pt-12 sm:pt-16 pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Pickup Menu</p>
          <h1 className="mt-3 font-display text-4xl sm:text-6xl font-semibold tracking-tight leading-[0.95]">
            Order for Pickup
          </h1>
          <p className="mt-4 max-w-xl text-sm sm:text-base text-cream/60 leading-relaxed">
            Tap items to build your order, then check out online in seconds — we'll fire it up fresh for your pickup.
            Prefer to talk? <a href={BRAND.phoneHref} className="text-gold underline-offset-2 hover:underline">Call us</a> and we'll take your order by phone.
            Everything is 100% Halal — and yes, everything's in stock.
          </p>
        </motion.div>
      </section>

      {/* Menu list */}
      <section className="mx-auto max-w-5xl px-5 sm:px-8 pb-44">
        {MENU.map((sec) => (
          <div key={sec.section} className="mt-12">
            <div className="flex items-center gap-4">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-cream">{sec.section}</h2>
              <span className="h-px flex-1 bg-white/10" />
            </div>
            <div className="mt-4">
              {sec.items.map((it, i) => (
                <Row key={i} item={it} n={i + 1} items={items} soldOut={soldOut} onAdd={handleAdd} />
              ))}
            </div>
            {sec.note && (
              <p className="mt-4 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-xs sm:text-sm text-cream/55">
                {sec.note}
              </p>
            )}
          </div>
        ))}

        <div className="mt-14 rounded-3xl border border-gold/20 bg-surface p-8 text-center gold-glow">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold">Ready to order?</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-cream/60">Check out online and we'll have it hot and ready for pickup.</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={openCheckout} data-testid="order-bottom-checkout" className="inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-4 text-base font-semibold text-ink transition-transform hover:-translate-y-0.5">
              <ShoppingBag size={20} /> Checkout Online
            </button>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-4 text-base font-semibold text-cream/80 transition-colors hover:border-gold/40 hover:text-cream">
              <Phone size={18} /> {BRAND.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Floating order trigger */}
      <div className="fixed inset-x-0 bottom-0 z-[95] p-3 sm:p-5 pointer-events-none">
        <div className="mx-auto flex max-w-5xl justify-end">
          {count > 0 && (
            <button
              onClick={() => setOpen(true)}
              data-testid="order-summary-toggle"
              className="pointer-events-auto flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-gold px-6 py-4 text-base font-semibold text-ink shadow-2xl gold-glow transition-transform hover:-translate-y-0.5"
            >
              <ShoppingBag size={20} />
              <span>View my order</span>
              <span className="grid h-6 min-w-6 place-items-center rounded-full bg-ink/20 px-1.5 text-sm">{count}</span>
              <span className="hidden sm:inline">· {fmt(subtotal)}</span>
            </button>
          )}
        </div>
      </div>

      {/* Order summary sheet */}
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              role="dialog"
              aria-modal="true"
              data-testid="order-summary"
              className="relative w-full sm:max-w-lg bg-surface border border-gold/20 rounded-t-[28px] sm:rounded-[28px] p-6 sm:p-7 gold-glow max-h-[85vh] flex flex-col"
              initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Your Order</p>
                  <h3 className="mt-1 font-display text-2xl font-semibold">Review &amp; checkout</h3>
                </div>
                <button onClick={() => setOpen(false)} aria-label="Close" data-testid="order-summary-close" className="h-10 w-10 grid place-items-center rounded-full border border-white/10 text-cream/70 hover:text-cream hover:border-gold/40 transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="mt-5 flex-1 overflow-y-auto -mx-1 px-1">
                {lines.map((v) => (
                  <div key={v.id} className="flex items-center gap-3 border-b border-white/8 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-cream">
                        {v.name}
                        {v.label && v.label !== "Reg" && v.label !== "Each" && <span className="text-cream/45"> · {v.label}</span>}
                      </p>
                      <p className="text-xs text-gold">{fmt(v.price)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setQty(v.id, v.qty - 1)} data-testid={`order-dec-${v.id}`} className="h-8 w-8 grid place-items-center rounded-full border border-white/12 text-cream hover:border-gold/40"><Minus size={15} /></button>
                      <span className="w-6 text-center text-sm font-semibold">{v.qty}</span>
                      <button onClick={() => setQty(v.id, v.qty + 1)} data-testid={`order-inc-${v.id}`} className="h-8 w-8 grid place-items-center rounded-full border border-white/12 text-cream hover:border-gold/40"><Plus size={15} /></button>
                    </div>
                    <span className="w-16 text-right text-sm font-semibold text-cream">{fmt(v.qty * v.price)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                <span className="text-sm text-cream/60">Subtotal</span>
                <span className="font-display text-2xl font-semibold text-gold" data-testid="order-total">{fmt(subtotal)}</span>
              </div>
              <p className="mt-1 text-[11px] text-cream/40">Taxes &amp; tip calculated at checkout.</p>

              <div className="mt-5 grid gap-3">
                <button onClick={goCheckout} data-testid="order-checkout" className="flex items-center justify-center gap-2.5 rounded-2xl bg-gold px-5 py-4 text-base font-semibold text-ink transition-transform hover:-translate-y-0.5">
                  <ShoppingBag size={20} /> Checkout · {fmt(subtotal)} <ArrowRight size={18} />
                </button>
                <a href={BRAND.phoneHref} data-testid="order-summary-call" className="text-center text-sm text-cream/55 underline-offset-2 transition-colors hover:text-gold hover:underline">
                  Or call to order · {BRAND.phoneDisplay}
                </a>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={copySummary} data-testid="order-copy" className="flex items-center justify-center gap-2 rounded-2xl border border-white/12 px-4 py-3 text-sm font-semibold text-cream hover:border-gold/40 transition-colors">
                    {copied ? <><Check size={16} className="text-gold" /> Copied!</> : <><Copy size={16} /> Copy list</>}
                  </button>
                  <button onClick={clear} data-testid="order-clear" className="flex items-center justify-center gap-2 rounded-2xl border border-white/12 px-4 py-3 text-sm font-semibold text-cream/70 hover:text-cream hover:border-white/25 transition-colors">
                    <Trash2 size={16} /> Clear
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <RatingPopup open={showRating} onClose={() => setShowRating(false)} />
      <Footer />
    </div>
  );
};

export default OrderPage;
